import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import UserIcon from 'icons/User.icon';
import { useTranslation } from 'react-i18next';
import TimelineNode from 'components/TimelineNode';
import { CollapsibleSection } from 'components/CollapsibleSection';
import {
  DATA_ROOM_NDA_SIGNING_STATUS,
  DataRoomNdaSigningStatus,
  DataRoomNdaSigningTimeline,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import { NDA_SIGNING_TRACK_EVENTS_COPY } from './data';
import useGenerateTimelineActions from './hooks';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './DataRoomNdaTimeline.styles.scss';

type DataRoomNdaTimelineProps = {
  className?: string;
  signingId: string;
  firstName: string;
  status: DataRoomNdaSigningStatus;
  numberOfNotSigned: number;
  timeline: DataRoomNdaSigningTimeline[];
  onSendReminder: (signingId: string) => void;
};

const DataRoomNdaTimeline: React.FC<DataRoomNdaTimelineProps> = (props) => {
  const {
    className,
    signingId,
    firstName,
    status,
    numberOfNotSigned,
    timeline = [],
    onSendReminder,
  } = props;

  const {
    currentUser: { email: currentUserEmail },
  } = useContext(CurrentUserContext);

  const classes = classNames(
    'data-room-nda-timeline',
    'white-panel',
    className,
  );

  const lastElement = timeline?.length - 1;

  const { t } = useTranslation();

  const ndaTimelineActions = useGenerateTimelineActions(
    signingId,
    onSendReminder,
  );

  const reversedTimeline = useMemo(
    () =>
      timeline
        .map(({ action, date, name, email, rejectionReason }, index) => (
          <TimelineNode
            key={action + email}
            date={date}
            text={t(NDA_SIGNING_TRACK_EVENTS_COPY[action], {
              email,
              rejectionReason,
              name: currentUserEmail === email ? t('you') : name,
            })}
          >
            {status === DATA_ROOM_NDA_SIGNING_STATUS.PENDING &&
              index === lastElement &&
              ndaTimelineActions[action]}
          </TimelineNode>
        ))
        .reverse(),
    [currentUserEmail, ndaTimelineActions, status, lastElement, timeline, t],
  );

  return (
    <CollapsibleSection
      className={classes}
      defaultOpen
      trigger={
        <p className="heading">
          <UserIcon />
          {t('userNDATimeline', { firstName })}
        </p>
      }
    >
      <div className="content">
        {reversedTimeline}

        {!!numberOfNotSigned && (
          <p>{t('peopleWaitingToSign', { count: numberOfNotSigned })}</p>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default DataRoomNdaTimeline;
